import React, { useEffect, useRef, useState } from 'react';
import { FaDownload, FaSpinner, FaUpload } from 'react-icons/fa';

const TEMPLATES = [
  { field: 'card_minion', label: 'Minion', width: 669, height: 1007 },
  { field: 'card_spell', label: 'Spell', width: 669, height: 947 },
  { field: 'card_weapon', label: 'Weapon', width: 669, height: 1007 },
  { field: 'card_herocard', label: 'Hero Card', width: 669, height: 1007 },
  { field: 'card_location', label: 'Location', width: 737, height: 1072 },
];

function validateTemplate(file, template) {
  if (!file) throw new Error(`Choose a ${template.label} template.`);
  if (!/\.png$/i.test(file.name) || (file.type && file.type !== 'image/png')) {
    throw new Error(`${template.label}: choose a PNG file.`);
  }
  if (file.size < 10000 || file.size > 1500000) {
    throw new Error(`${template.label}: the file must be between 10 KB and 1.5 MB.`);
  }

  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      if (image.naturalWidth !== template.width || image.naturalHeight !== template.height) {
        reject(new Error(`${template.label}: the image must be ${template.width} × ${template.height} pixels.`));
      } else {
        resolve();
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`${template.label}: the file could not be read as an image.`));
    };
    image.src = url;
  });
}

export default function ClassUploader({ onSuccess }) {
  const [files, setFiles] = useState({});
  const [className, setClassName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const uploadRef = useRef(null);

  useEffect(() => () => uploadRef.current?.abort(), []);

  const submitClass = async (event) => {
    event.preventDefault();
    if (uploadRef.current) return;
    setError('');
    if (!className.trim()) {
      setError('Give your class a name.');
      return;
    }

    const controller = new AbortController();
    uploadRef.current = controller;
    setBusy(true);
    try {
      for (const template of TEMPLATES) {
        await validateTemplate(files[template.field], template);
        if (controller.signal.aborted) return;
      }
      const formData = new FormData();
      for (const template of TEMPLATES) {
        // The legacy endpoint requires a lowercase .png extension.
        formData.append(template.field, files[template.field], `${template.field}.png`);
      }
      formData.append('emblemName', className.trim());
      formData.append('emblemPublic', isPublic ? 'true' : 'false');

      const response = await fetch('/ajax/class_recieve.php', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || payload?.status !== 'success') {
        throw new Error(payload?.code || payload?.error || 'Class upload failed. Please try again.');
      }
      if (!controller.signal.aborted) onSuccess?.();
    } catch (uploadError) {
      if (!controller.signal.aborted) setError(uploadError.message || 'Class upload failed. Please try again.');
    } finally {
      uploadRef.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  return (
    <form className="p-3 p-md-4 h-100 overflow-auto" onSubmit={submitClass}>
      <section className="mb-4">
        <h6 className="text-warning fw-bold">1. Download the templates</h6>
        <p className="small text-muted">
          Download the archive with example templates and make your class in image editing program of your choice. Text and other overlay placement positions are not user customizable and will be applied as if it was any regular class, only the example templates should be used. Do not remove empty space around the card, remove transparency, remove or modify the metal ring around the cards art and similiar Hearthstone elements or change image dimensions.
        </p>
        <a className="btn btn-outline-warning" href="/images/hearthstone.zip" download>
          <FaDownload className="me-2" /> Download Example Templates
        </a>
      </section>

      <fieldset className="border-0 p-0 m-0" disabled={busy}>
        <section className="mb-4">
          <h6 className="text-warning fw-bold">2. Select your files</h6>
          <p className="small text-muted">
            All five templates have to be submited. Hero Power cards are the same for every class and as such are not customizable.
          </p>
          <div className="row g-3">
            {TEMPLATES.map((template) => (
              <div className="col-12 col-md-6" key={template.field}>
                <label className="form-label small" htmlFor={`class-upload-${template.field}`}>
                  {template.label} ({template.width} × {template.height} PNG)
                </label>
                <input
                  id={`class-upload-${template.field}`}
                  type="file"
                  accept=".png,image/png"
                  required
                  className="form-control bg-dark text-white border-secondary"
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    setFiles((previous) => ({ ...previous, [template.field]: file }));
                    setError('');
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="mb-3">
          <h6 className="text-warning fw-bold">3. Finalize your class</h6>
          <label className="form-label small" htmlFor="class-upload-name">Class name</label>
          <input
            id="class-upload-name"
            type="text"
            required
            className="form-control bg-dark text-white border-secondary"
            placeholder="Name your class"
            value={className}
            onChange={(event) => setClassName(event.target.value)}
          />
          <div className="form-check mt-3">
            <input
              id="class-upload-public"
              type="checkbox"
              className="form-check-input"
              checked={isPublic}
              onChange={(event) => setIsPublic(event.target.checked)}
            />
            <label className="form-check-label small" htmlFor="class-upload-public">
              Make this class available for everyone to use
            </label>
          </div>
        </section>

        {error && <div className="alert alert-danger py-2 small" role="alert">{error}</div>}
        <button
          type="submit"
          className="btn btn-warning fw-bold w-100"
          disabled={busy || !className.trim() || TEMPLATES.some((template) => !files[template.field])}
        >
          {busy ? <FaSpinner className="fa-spin me-2" /> : <FaUpload className="me-2" />}
          {busy ? 'Uploading…' : 'Upload class'}
        </button>
      </fieldset>
    </form>
  );
}
